import { useState } from 'react';
import { AlertTriangle, CheckCircle2, CircleAlert, Info, Wallet } from 'lucide-react';

import { Navigate } from 'react-router-dom';

import { usePaymentInfo } from '@/api/hooks/usePayments';
import { useSession } from '@/auth/session';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ClaimsHistoryList } from '@/features/subscription/ClaimsHistoryList';
import { PaymentMethodCard } from '@/features/subscription/PaymentMethodCard';
import { SubmitClaimDialog } from '@/features/subscription/SubmitClaimDialog';
import { computeBanner } from '@/features/subscription/subscriptionBanner';
import { cn, formatDate, money } from '@/lib/utils';

// Text colour matches each tone's own status token, not a pale tint of it —
// a 10% tinted background needs full-strength text to stay readable.
const BANNER_STYLE = {
  danger: { bg: 'border-status-expired/40 bg-status-expired/10 text-status-expired', Icon: CircleAlert },
  warning: { bg: 'border-status-low/40 bg-status-low/10 text-gold-700', Icon: AlertTriangle },
  info: { bg: 'border-status-expiring/40 bg-status-expiring/10 text-sky-deep', Icon: Info },
  success: { bg: 'border-status-ok/40 bg-status-ok/10 text-mint-deep', Icon: CheckCircle2 },
} as const;

export function SubscriptionPage() {
  const { isSuperadmin } = useSession();
  // Superadmins have no pharmacy — the endpoint this page reads from rejects
  // them outright, so skip the request entirely and send them to their own
  // console instead of surfacing an error.
  const { data, isLoading } = usePaymentInfo(!isSuperadmin);
  const [claimOpen, setClaimOpen] = useState(false);

  if (isSuperadmin) return <Navigate to="/superadmin" replace />;

  if (isLoading || !data) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <Skeleton className="h-16" />
        <Skeleton className="h-32" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  const { pharmacy, paymentInfo, claims } = data;
  const banner = computeBanner(pharmacy);
  const style = BANNER_STYLE[banner.tone];
  const showPricing = pharmacy.setupFee > 0 || pharmacy.monthlyFee > 0;

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <h1 className="font-display text-3xl">Subscription & payment</h1>

      <div className={cn('flex items-center gap-3 rounded-lg border p-3.5 text-sm', style.bg)}>
        <style.Icon className="h-4 w-4 shrink-0" />
        {banner.text}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Your pharmacy</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-muted-foreground">Pharmacy</p>
            <p className="font-semibold">{pharmacy.name}</p>
            <p className="text-xs text-muted-foreground">/{pharmacy.slug}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Current plan</p>
            <p className="font-semibold">{pharmacy.plan}</p>
            <p className="text-xs text-muted-foreground">Status: {pharmacy.status}</p>
          </div>
        </CardContent>
      </Card>

      {showPricing && (
        <Card className="border-primary/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Wallet className="h-4 w-4" /> Pricing
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pharmacy.setupFee > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Setup fee (one-time)</span>
                <span className="tabular flex items-center gap-1.5">
                  {money(pharmacy.setupFee)}
                  {pharmacy.setupFeePaid && <Badge variant="ok">Paid</Badge>}
                </span>
              </div>
            )}
            {pharmacy.monthlyFee > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Monthly fee</span>
                <span className="tabular">{money(pharmacy.monthlyFee)}</span>
              </div>
            )}
            <div className="flex items-center justify-between border-t border-border pt-2 text-base font-semibold">
              <span>Amount due</span>
              <span className="tabular">{money(pharmacy.amountDue)}</span>
            </div>
            <Button className="mt-2 w-full" onClick={() => setClaimOpen(true)}>
              I've paid — submit proof
            </Button>
          </CardContent>
        </Card>
      )}

      <PaymentMethodCard info={paymentInfo} />

      {!showPricing && (
        <Button variant="outline" className="w-full" onClick={() => setClaimOpen(true)}>
          Submit a payment claim
        </Button>
      )}

      <ClaimsHistoryList claims={claims} />

      <p className="text-center text-xs text-muted-foreground">
        Trial ends {formatDate(pharmacy.trialEndsAt)}
        {pharmacy.subscriptionEndsAt && ` · Subscription until ${formatDate(pharmacy.subscriptionEndsAt)}`}
      </p>

      <SubmitClaimDialog open={claimOpen} onOpenChange={setClaimOpen} suggestedAmount={pharmacy.amountDue} />
    </div>
  );
}
